import { useMemo } from 'react'
import { Chip, Divider, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { FiberManualRecord as DotIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useReleaseCheckStatus } from '../../api/release-check-api'
import { CATEGORY_ORDER, ReleaseCheckItem } from '../../types/ReleaseCheckItem'
import { SUB_ORCHESTRATED_CATEGORIES } from '../../types/RunStatus'
import { isRunningState } from './CategoryRow'
import { hasDiscrepancies } from './itemVisibility'
import { PanelKey } from './usePanelState'

interface PanelNavProps {
  panel: PanelKey
  items: ReleaseCheckItem[]
  onSelect: (panel: PanelKey) => void
}

const PanelNav = ({ panel, items, onSelect }: PanelNavProps) => {
  const { t } = useTranslation()
  const [status] = useReleaseCheckStatus()

  const anyRunning =
    isRunningState(status?.state) ||
    SUB_ORCHESTRATED_CATEGORIES.some((c) => isRunningState(status?.categories?.[c]?.state))

  // Badges always count non-ignored discrepancies, independent of the
  // Include ignored / Include compliant view filters.
  const { counts, total } = useMemo(() => {
    const c: Record<string, number> = {}
    let sum = 0
    for (const it of items) {
      if (!it.ignored && hasDiscrepancies(it)) {
        c[it.category] = (c[it.category] ?? 0) + 1
        sum += 1
      }
    }
    return { counts: c, total: sum }
  }, [items])

  return (
    <List dense disablePadding>
      <ListItemButton selected={panel === 'status'} onClick={() => onSelect('status')}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <DotIcon fontSize="small" color={anyRunning ? 'warning' : 'disabled'} />
        </ListItemIcon>
        <ListItemText primary={t('releaseCheck.panel.status', 'Status')} />
      </ListItemButton>
      <Divider />
      <ListItemButton selected={panel === 'all'} onClick={() => onSelect('all')}>
        <ListItemText primary={t('releaseCheck.panel.all', 'All discrepancies')} />
        <Chip size="small" label={total} color={total > 0 ? 'warning' : 'default'} />
      </ListItemButton>
      {CATEGORY_ORDER.map((cat) => {
        const count = counts[cat] ?? 0
        return (
          <ListItemButton key={cat} selected={panel === cat} onClick={() => onSelect(cat)}>
            <ListItemText primary={t(`releaseCheck.category.${cat}`, cat)} />
            <Chip size="small" label={count} color={count > 0 ? 'warning' : 'default'} />
          </ListItemButton>
        )
      })}
    </List>
  )
}

export default PanelNav
